import { Dropdown, HorizontalCard } from '@atlas/ui-kit'
import {
	Button as AriaButton,
	ListBox as AriaListBox,
	ListBoxItem as AriaListBoxItem,
	Popover as AriaPopover,
	Select as AriaSelect,
} from 'react-aria-components'
import { initials } from '@/lib/labels'
import type { DesignerOption } from '../AssignDesignerCard'

const TITLE = 'Assigned designer'

/**
 * Assigned designer as the Full Path screen draws it (377:41747): a white card holding the
 * kit's designer HorizontalCard. The Figma draws the chevron but no menu, so until the design
 * team draws one, pressing the card opens a list in the kit Dropdown's popover language, and
 * picking a person assigns them at once. With nobody assigned yet, the kit Dropdown asks for one.
 */
export function AssignedDesignerCard({
	options,
	value,
	onChange,
}: {
	options: DesignerOption[]
	/** The assigned designer's user id, or null while the order has none. */
	value: string | null
	onChange: (userId: string) => void
}) {
	const current = options.find((option) => option.userId === value)
	const select = {
		'aria-label': 'Designer',
		selectedKey: value,
		onSelectionChange: (key: unknown) => onChange(String(key)),
	}
	return (
		<section aria-label={TITLE} className="flex flex-col gap-6 rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans">
			<h2 className="text-atlas-h3 text-atlas-dark">{TITLE}</h2>
			{current ? (
				<AriaSelect {...select} className="flex w-full">
					<AriaButton className="w-full cursor-pointer rounded-atlas-lg text-left outline-none data-focus-visible:outline-2 data-focus-visible:outline-atlas-accent">
						<HorizontalCard
							variant="designer"
							name={current.name}
							role={current.role ?? ''}
							initials={initials(current.name)}
							className="w-full"
						/>
					</AriaButton>
					<AriaPopover
						offset={4}
						className="min-w-(--trigger-width) rounded-atlas-md bg-atlas-light p-1 shadow-atlas-drop-2 outline-none"
					>
						<AriaListBox items={options.map((option) => ({ ...option, id: option.userId }))} className="outline-none">
							{(option) => (
								<AriaListBoxItem
									id={option.userId}
									textValue={option.name}
									className="cursor-pointer rounded-atlas-sm px-3 py-2 text-atlas-label-medium text-atlas-dark outline-none data-focused:bg-atlas-background data-selected:text-atlas-accent"
								>
									{option.role ? `${option.name} · ${option.role}` : option.name}
								</AriaListBoxItem>
							)}
						</AriaListBox>
					</AriaPopover>
				</AriaSelect>
			) : (
				<Dropdown
					{...select}
					size="l"
					placeholder="Choose a designer"
					items={options.map((option) => ({ id: option.userId, label: option.name }))}
				/>
			)}
		</section>
	)
}
